<template>
    <div
        class="wa-contact-item"
        :class="{
            'wa-contact-item--active': isActive,
            'wa-contact-item--unread': contact.unread,
        }"
        @click="$emit('click', contact)"
    >
        <div class="wa-contact-item__avatar">
            <div class="wa-contact-item__avatar-circle" :style="avatarStyle">
                <img
                    v-if="avatarUrl"
                    :src="avatarUrl"
                    class="wa-contact-item__avatar-img"
                    @error="onAvatarError"
                    alt=""
                />
                <span v-else class="wa-contact-item__avatar-initials">{{ initials }}</span>
            </div>
        </div>

        <div class="wa-contact-item__content">
            <div class="wa-contact-item__row wa-contact-item__row--top">
                <span class="wa-contact-item__name">{{ displayName }}</span>
                <span class="wa-contact-item__time" :class="{ 'wa-contact-item__time--unread': contact.unread }">
                    {{ displayTime }}
                </span>
            </div>
            <div class="wa-contact-item__row wa-contact-item__row--bottom">
                <div class="wa-contact-item__last-msg">
                    <span v-if="lastMessageIcon" class="wa-contact-item__msg-icon material-icons">{{ lastMessageIcon }}</span>
                    <span class="wa-contact-item__msg-text">{{ lastMessageText }}</span>
                </div>
                <div class="wa-contact-item__indicators">
                    <span v-if="contact.vipCustomer" class="wa-contact-item__vip" v-tippy="{ content: $t('whatsappUi.vipCustomerTooltip') }">
                        <i class="material-icons">star</i>
                    </span>
                    <span
                        v-if="contact.unread && unreadCount > 0"
                        class="wa-contact-item__unread-badge"
                    >
                        {{ unreadCount > 99 ? $t("whatsappUi.unreadOverflow") : unreadCount }}
                    </span>
                    <span v-else-if="contact.unread" class="wa-contact-item__unread-dot" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import moment from "moment";
import { maskContactInfo, isMaskContactInfo } from "../../../../utils/utils";

const AVATAR_COLORS = [
    "#128C7E", "#25D366", "#075E54", "#34B7F1",
    "#00BCD4", "#4CAF50", "#8BC34A", "#FF9800",
    "#F44336", "#9C27B0", "#3F51B5", "#2196F3",
];

const LAST_MESSAGE_ICONS = {
    "user-media": "perm_media",
    "visitor-media": "perm_media",
    "whatsapp-user-media": "perm_media",
    "chatbot-image": "image",
    "chatbot-file": "description",
    "user-email": "mail",
    "user-sms": "sms",
    "whatsapp-business-api-template": "article",
    "voice-agent-call": "phone",
    "visitor-location": "location_on",
    "user-location": "location_on",
};

const LAST_MESSAGE_TEXTS = {
    "user-media": "Medya",
    "visitor-media": "Medya",
    "whatsapp-user-media": "Medya",
    "chatbot-image": "Fotoğraf",
    "chatbot-file": "Belge",
    "user-email": "E-posta",
    "user-sms": "SMS",
    "whatsapp-business-api-template": "Şablon mesajı",
    "voice-agent-call": "Yapay Zeka Araması",
    "visitor-location": "Konum",
    "user-location": "Konum",
};

export default {
    name: "WAContactListItem",
    props: {
        contact: {
            type: Object,
            required: true,
        },
        selectedContact: {
            type: Object,
            default: null,
        },
    },
    data: () => ({
        avatarError: false,
    }),
    computed: {
        isActive() {
            return !!(this.selectedContact && this.selectedContact._id === this.contact._id);
        },
        displayName() {
            const contact = this.contact;
            if (isMaskContactInfo()) {
                return maskContactInfo(contact.fullname || contact.name || contact.whatsappNumber || "Bilinmeyen");
            }
            return contact.fullname || contact.name || contact.whatsappNumber || contact.email || "Bilinmeyen";
        },
        initials() {
            const name = this.displayName || "?";
            return name
                .split(" ")
                .slice(0, 2)
                .map((w) => w[0])
                .join("")
                .toUpperCase();
        },
        avatarUrl() {
            if (this.avatarError) return null;
            const img = this.$_.get(this.contact, "whatsappInfo.profilePicThumbObj.img");
            if (img) return img;
            const profilePic = this.$_.get(this.contact, "profilePicture");
            return profilePic || null;
        },
        avatarStyle() {
            if (this.avatarUrl) return {};
            const name = this.displayName || "?";
            const idx = name.charCodeAt(0) % AVATAR_COLORS.length;
            return { backgroundColor: AVATAR_COLORS[idx] };
        },
        displayTime() {
            const date = this.$_.get(this.contact, "lastConversation.lastMessageDate")
                || this.$_.get(this.contact, "lastMessage.createdDate")
                || this.$_.get(this.contact, "updatedDate");
            if (!date) return "";
            const m = moment(date);
            const now = moment();
            if (m.isSame(now, "day")) return m.format("HH:mm");
            if (m.isSame(now.clone().subtract(1, "day"), "day")) return "Dün";
            if (m.isSame(now, "week")) return m.format("dddd");
            return m.format("DD.MM.YY");
        },
        lastMessageText() {
            const lastMsg = this.contact.lastMessage;
            if (!lastMsg) return "";
            const type = lastMsg.messageType;
            if (LAST_MESSAGE_TEXTS[type]) return LAST_MESSAGE_TEXTS[type];
            if (lastMsg.asset) return "Medya";
            if (lastMsg.description) {
                const plain = lastMsg.description.replace(/<[^>]+>/g, "").trim();
                return plain.length > 60 ? plain.slice(0, 60) + "…" : plain;
            }
            return "";
        },
        lastMessageIcon() {
            const lastMsg = this.contact.lastMessage;
            if (!lastMsg) return null;
            return LAST_MESSAGE_ICONS[lastMsg.messageType] || null;
        },
        unreadCount() {
            return this.contact.unreadCount || 0;
        },
    },
    methods: {
        onAvatarError() {
            this.avatarError = true;
        },
    },
};
</script>

<style lang="scss" scoped>
.wa-contact-item {
    display: flex;
    align-items: center;
    padding: 10px 16px;
    cursor: pointer;
    transition: background-color 0.1s;
    min-height: 72px;
    border-bottom: 1px solid rgba(134, 150, 160, 0.15);

    &:hover {
        background-color: #f5f6f6;
    }

    &--active {
        background-color: #f0f2f5;
    }

    &--unread {
        .wa-contact-item__name {
            font-weight: 600;
            color: #111b21;
        }
    }

    &__avatar {
        flex-shrink: 0;
        margin-right: 12px;
    }

    &__avatar-circle {
        width: 49px;
        height: 49px;
        border-radius: 50%;
        background-color: #dfe5e7;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
    }

    &__avatar-img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }

    &__avatar-initials {
        color: #fff;
        font-size: 18px;
        font-weight: 500;
        line-height: 1;
    }

    &__content {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 3px;
    }

    &__row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;

        &--bottom {
            align-items: center;
        }
    }

    &__name {
        font-size: 17px;
        font-weight: 400;
        color: #111b21;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        flex: 1;
    }

    &__time {
        font-size: 12px;
        color: #667781;
        flex-shrink: 0;
        white-space: nowrap;

        &--unread {
            color: #25D366;
            font-weight: 500;
        }
    }

    &__last-msg {
        display: flex;
        align-items: center;
        gap: 5px;
        flex: 1;
        min-width: 0;
        color: #667781;
        font-size: 14px;
    }

    &__msg-icon {
        font-size: 14px !important;
        color: #667781;
        flex-shrink: 0;
    }

    &__msg-text {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    &__indicators {
        display: flex;
        align-items: center;
        gap: 5px;
        flex-shrink: 0;
    }

    &__vip {
        color: #F9A000;
        font-size: 14px;
        display: flex;
        align-items: center;

        i {
            font-size: 14px !important;
        }
    }

    &__unread-badge {
        background-color: #25D366;
        color: #fff;
        border-radius: 12px;
        font-size: 11px;
        font-weight: 700;
        min-width: 20px;
        height: 20px;
        line-height: 20px;
        text-align: center;
        padding: 0 5px;
    }

    &__unread-dot {
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background-color: #25D366;
        flex-shrink: 0;
    }
}
</style>
